import axios from 'axios';

// ========== CONFIGURATION ==========
// Use proxy in development, direct in production
const USE_PROXY = process.env.NODE_ENV === 'development';

// For axios calls (callAPI function)
const PROXY_API_URL = '/api/index.php';
const DIRECT_API_URL = 'https://nlngraclub.com/nlnapi/index.php';
const PHP_API_URL = USE_PROXY ? PROXY_API_URL : DIRECT_API_URL;

// Helper function to get fetch URL (always uses proxy in development)
const getFetchUrl = (endpoint) => {
    const timestamp = Date.now();
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isLocalhost) {
        return `/api/index.php?url=${endpoint}&_=${timestamp}`;
    }
    return `https://nlngraclub.com/nlnapi/index.php?url=${endpoint}&_=${timestamp}`;
};

console.log(`🔧 API Configuration:`);
console.log(`   Environment: ${process.env.NODE_ENV}`);
console.log(`   Using Proxy: ${USE_PROXY ? 'Yes' : 'No'}`);
console.log(`   API URL: ${PHP_API_URL}`);

// ========== HELPER FUNCTIONS ==========
const callAPI = async(endpoint, data = null, method = 'GET') => {
    try {
        const timestamp = Date.now();
        let url = `${PHP_API_URL}?url=${endpoint}&_=${timestamp}&nocache=${timestamp}`;

        console.log(`🌐 Calling API: ${url}`, method, data ? data : '');

        const config = {
            method: method,
            url: url,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
                'Expires': '0'
            },
            timeout: 30000,
            withCredentials: false,
        };

        const token = localStorage.getItem('nlngra_jwt');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        if (method === 'POST' && data) {
            config.data = data;
        } else if (method === 'GET' && data) {
            config.params = data;
        }

        const response = await axios(config);

        console.log(`✅ API call successful for ${endpoint}:`, response.status);

        return {
            success: true,
            data: response.data,
            status: response.status,
            headers: response.headers
        };

    } catch (error) {
        console.error(`❌ API call failed for ${endpoint}:`, error.message);

        let errorMessage = error.message;
        let errorCode = 'UNKNOWN_ERROR';
        let responseData = null;

        if (error.response) {
            errorMessage = `Server Error: ${error.response.status} - ${error.response.statusText}`;
            errorCode = `HTTP_${error.response.status}`;
            responseData = error.response.data;
            console.error('Response data:', responseData);
        } else if (error.request) {
            errorMessage = 'No response received from server.';
            errorCode = 'NO_RESPONSE';
            console.error('Request was made but no response received');
        }

        return {
            success: false,
            error: errorMessage,
            code: errorCode,
            data: responseData,
            endpoint: endpoint,
            timestamp: new Date().toISOString()
        };
    }
};

// ========== AUTHENTICATION API ==========
export const authAPI = {
    login: async(credentials) => {
    console.log('🔐 Attempting login...', {
        email: credentials.email,
        password_length: credentials.password?.length || 0
    });

    try {
        // Use proxy in development, direct in production
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        
        const url = isLocalhost
            ? '/api/index.php?url=login'  // Development - uses proxy
            : 'https://nlngraclub.com/nlnapi/index.php?url=login'; // Production - direct

        console.log('🔄 Sending login request to:', url);

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({
                email: credentials.email?.trim() || '',
                password: credentials.password || ''
            })
        });

        const responseText = await response.text();
        console.log('📥 Raw login response:', responseText.substring(0, 500));

        let data;
        try {
            let cleanResponse = responseText;
            cleanResponse = cleanResponse.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '');
            cleanResponse = cleanResponse.replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '');
            cleanResponse = cleanResponse.replace(/Cannot modify header information.*?\n/g, '');

            const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                data = JSON.parse(jsonMatch[0]);
            } else {
                data = JSON.parse(cleanResponse.trim());
            }
        } catch (parseError) {
            console.error('Failed to parse JSON:', parseError);
            return {
                success: false,
                message: 'Server returned invalid response',
                rawResponse: responseText.substring(0, 200)
            };
        }

        console.log('📥 Parsed login response:', data);

        if (data.success) {
            console.log('✅ Login successful!');

            if (data.token) {
                localStorage.setItem('nlngra_token', data.token);
                localStorage.setItem('nlngra_jwt', data.token);
                localStorage.setItem('nlngra_session', data.session_token || '');
            }

            if (data.user) {
                localStorage.setItem('nlngra_user', JSON.stringify(data.user));
            }

            return {
                success: true,
                message: data.message || 'Login successful',
                token: data.token,
                session_token: data.session_token,
                user: data.user,
                dependents: data.dependents || [],
                expires_at: data.expires_at
            };
        } else {
            console.log('❌ Login failed:', data.message);
            return {
                success: false,
                message: data.message || 'Login failed',
                data: data
            };
        }
    } catch (error) {
        console.error('❌ Login error:', error);
        return {
            success: false,
            message: 'Network error: ' + error.message
        };
    }
},

    register: async(memberData) => {
        console.log('📝 Registering new member...', memberData);

        try {
            const url = getFetchUrl('register');

            console.log('🔄 Using proxy to:', url);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(memberData)
            });

            console.log('📥 Response status:', response.status);

            const responseText = await response.text();
            console.log('📥 Raw response (first 500 chars):', responseText.substring(0, 500));

            let cleanResponse = responseText;
            cleanResponse = cleanResponse.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '');
            cleanResponse = cleanResponse.replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '');
            cleanResponse = cleanResponse.replace(/Cannot modify header information.*?\n/g, '');

            const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);

            let data;
            if (jsonMatch) {
                try {
                    data = JSON.parse(jsonMatch[0]);
                    console.log('✅ Successfully extracted and parsed JSON:', data);
                } catch (parseError) {
                    console.error('Failed to parse extracted JSON:', parseError);
                    return {
                        success: false,
                        message: 'Server returned invalid JSON',
                        rawResponse: responseText.substring(0, 200)
                    };
                }
            } else {
                try {
                    data = JSON.parse(cleanResponse.trim());
                } catch (e) {
                    console.error('No JSON found in response');
                    return {
                        success: false,
                        message: 'Server returned invalid response',
                        rawResponse: responseText.substring(0, 200)
                    };
                }
            }

            console.log('📥 Parsed response:', data);

            if (data.success) {
                console.log('✅ Registration successful!');

                return {
                    success: true,
                    message: data.message || 'Registration successful! Your account is pending approval.',
                    member_id: data.member_id,
                    membership_number: data.membership_number,
                    user: data.user || {
                        first_name: memberData.first_name,
                        last_name: memberData.last_name,
                        full_name: memberData.first_name + ' ' + memberData.last_name,
                        email: memberData.email,
                        phone: memberData.phone
                    },
                    timestamp: new Date().toISOString()
                };
            } else {
                console.log('❌ Registration failed:', data.message);

                let errorMsg = data.error || data.message || 'Registration failed';

                if (errorMsg.includes('Email already') || errorMsg.includes('already registered')) {
                    errorMsg = 'This email is already registered. Please use a different email or login.';
                }

                return {
                    success: false,
                    message: errorMsg,
                    data: data
                };
            }

        } catch (error) {
            console.error('❌ Registration error:', error);
            return {
                success: false,
                message: 'Network error: ' + error.message,
                error: error.message
            };
        }
    },
      // ========== GET PRIMARY MEMBERS FOR DROPDOWN ==========
    getPrimaryMembers: async() => {
        console.log('👥 Fetching primary members for dropdown...');

        try {
            const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            const url = isLocalhost
                ? '/api/index.php?url=primary-members'
                : 'https://nlngraclub.com/nlnapi/index.php?url=primary-members';

            console.log('🔄 Fetching primary members from:', url);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            const data = await response.json();
            console.log('📥 Primary members response:', data);

            if (data.success) {
                return {
                    success: true,
                    members: data.members || [],
                    count: data.count || 0,
                    message: 'Primary members loaded successfully'
                };
            } else {
                return {
                    success: false,
                    members: [],
                    message: data.message || 'Failed to load primary members'
                };
            }

        } catch (error) {
            console.error('❌ Get primary members error:', error);
            return {
                success: false,
                members: [],
                message: 'Network error: ' + error.message
            };
        }
    },
    checkEmail: async(email) => {
        console.log('📧 Checking email availability:', email);

        try {
            const url = getFetchUrl('check-email');

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ email: email })
            });

            const data = await response.json();
            console.log('Email check result:', data);

            return {
                exists: data.exists || false,
                success: true
            };

        } catch (error) {
            console.error('Email check error:', error);
            return {
                exists: false,
                success: false,
                error: error.message
            };
        }
    },

    checkPhone: async(phone) => {
        console.log('📱 Checking phone availability:', phone);

        try {
            const url = getFetchUrl('check-phone');

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ phone: phone })
            });

            const data = await response.json();
            console.log('Phone check result:', data);

            return {
                exists: data.exists || false,
                success: true
            };

        } catch (error) {
            console.error('Phone check error:', error);
            return {
                exists: false,
                success: false,
                error: error.message
            };
        }
    },

    searchPrimaryMembers: async(searchTerm) => {
        console.log('🔍 Searching primary members:', searchTerm);

        try {
            const url = getFetchUrl('search-primary-members');

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ search: searchTerm })
            });

            const data = await response.json();
            console.log('Search results:', data);

            if (data.success && data.members) {
                return {
                    success: true,
                    members: data.members,
                    count: data.count || data.members.length
                };
            } else {
                return {
                    success: false,
                    members: [],
                    message: data.message || 'No members found'
                };
            }

        } catch (error) {
            console.error('Primary member search error:', error);
            return {
                success: false,
                members: [],
                error: error.message
            };
        }
    },

    verify: async() => {
        const token = localStorage.getItem('nlngra_jwt');
        if (!token) {
            return { success: false, authenticated: false, message: 'No session found' };
        }

        console.log('🔍 Verifying session...');

        try {
            const url = getFetchUrl('verify');

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (data.success && data.authenticated) {
                return {
                    success: true,
                    authenticated: true,
                    user: data.user,
                    payload: data.payload
                };
            } else {
                return {
                    success: false,
                    authenticated: false,
                    message: data.message || 'Session verification failed'
                };
            }
        } catch (error) {
            console.error('Session verification error:', error);
            return {
                success: false,
                authenticated: false,
                message: 'Session verification failed'
            };
        }
    },

    getDashboard: async() => {
        console.log('📊 Getting dashboard data...');

        try {
            const url = getFetchUrl('dashboard');
            const token = localStorage.getItem('nlngra_jwt');

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                }
            });

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    dashboard: data.dashboard,
                    message: 'Dashboard loaded successfully'
                };
            } else {
                return {
                    success: false,
                    message: data.message || 'Failed to load dashboard data'
                };
            }
        } catch (error) {
            console.error('Dashboard error:', error);
            return {
                success: false,
                message: 'Failed to load dashboard data'
            };
        }
    },

    updateProfile: async(profileData) => {
        console.log('✏️ Updating profile...', profileData);

        try {
            const url = getFetchUrl('profile');
            const token = localStorage.getItem('nlngra_jwt');

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify(profileData)
            });

            const data = await response.json();

            if (data.success) {
                if (data.member) {
                    localStorage.setItem('nlngra_user', JSON.stringify(data.member));
                }

                return {
                    success: true,
                    message: data.message || 'Profile updated successfully',
                    member: data.member
                };
            } else {
                return {
                    success: false,
                    message: data.error || data.message || 'Failed to update profile'
                };
            }
        } catch (error) {
            console.error('Update profile error:', error);
            return {
                success: false,
                message: 'Failed to update profile'
            };
        }
    },

    registerWithActivation: async(memberData, evidenceFile = null) => {
        console.log('📝 Registering with activation workflow...', memberData);

        try {
            const url = USE_PROXY ?
                '/api/register_v2.php' :
                'https://nlngraclub.com/nlnapi/register_v2.php';

            let formData = new FormData();
            formData.append('member_data', JSON.stringify(memberData));

            if (evidenceFile && memberData.membership_category === 'associate_member') {
                formData.append('evidence', evidenceFile);
            }

            const response = await fetch(url, {
                method: 'POST',
                body: formData
            });

            const result = await response.json();
            console.log('Registration result:', result);

            if (result.success) {
                localStorage.setItem('pending_registration_email', memberData.email);
                localStorage.setItem('pending_registration_id', result.member_id);

                return {
                    success: true,
                    message: result.message,
                    member_id: result.member_id,
                    membership_number: result.membership_number,
                    next_step: result.next_step,
                    requires_evidence: result.requires_evidence || false
                };
            } else {
                return {
                    success: false,
                    message: result.message || 'Registration failed'
                };
            }

        } catch (error) {
            console.error('Registration error:', error);
            return {
                success: false,
                message: 'Network error: ' + error.message
            };
        }
    },

    resendActivationEmail: async(email) => {
        console.log('📧 Resending activation email to:', email);

        try {
            const url = USE_PROXY ?
                '/api/resend_activation.php' :
                'https://nlngraclub.com/nlnapi/resend_activation.php';

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ email: email })
            });

            const result = await response.json();
            return result;

        } catch (error) {
            console.error('Resend activation error:', error);
            return {
                success: false,
                message: 'Failed to resend activation email'
            };
        }
    },

    checkAccountStatus: async(email) => {
        console.log('🔍 Checking account status for:', email);

        try {
            const url = USE_PROXY ?
                '/api/check_status.php' :
                'https://nlngraclub.com/nlnapi/check_status.php';

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ email: email })
            });

            const result = await response.json();
            return result;

        } catch (error) {
            console.error('Check status error:', error);
            return {
                success: false,
                message: 'Failed to check account status'
            };
        }
    },

    uploadEvidence: async(memberId, evidenceFile) => {
        console.log('📎 Uploading evidence for associate member:', memberId);

        try {
            const url = USE_PROXY ?
                '/api/upload_evidence.php' :
                'https://nlngraclub.com/nlnapi/upload_evidence.php';

            const formData = new FormData();
            formData.append('member_id', memberId);
            formData.append('evidence', evidenceFile);

            const token = localStorage.getItem('nlngra_jwt');

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': token ? `Bearer ${token}` : ''
                },
                body: formData
            });

            const result = await response.json();
            return result;

        } catch (error) {
            console.error('Upload evidence error:', error);
            return {
                success: false,
                message: 'Failed to upload evidence'
            };
        }
    },

    getPendingApprovals: async() => {
        console.log('👨‍👩‍👧 Getting pending parent approvals...');

        try {
            const url = USE_PROXY ?
                '/api/get_pending_approvals.php' :
                'https://nlngraclub.com/nlnapi/get_pending_approvals.php';

            const token = localStorage.getItem('nlngra_jwt');

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const result = await response.json();
            return result;

        } catch (error) {
            console.error('Get pending approvals error:', error);
            return {
                success: false,
                pending_approvals: []
            };
        }
    },

    checkEmailActivated: async() => {
        const user = authAPI.getCurrentUser();
        if (!user || !user.id) return false;

        try {
            const url = USE_PROXY ?
                '/api/check_email_status.php' :
                'https://nlngraclub.com/nlnapi/check_email_status.php';

            const token = localStorage.getItem('nlngra_jwt');

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const result = await response.json();
            return result.email_activated === true;

        } catch (error) {
            console.error('Check email status error:', error);
            return false;
        }
    },

    // ========== FIXED: Forgot Password ==========
    forgotPassword: async(email) => {
        console.log('🔑 Forgot password for:', email);

        try {
            const tempPassword = 'test123';

            // Use the direct PHP file URL
            const url = 'https://nlngraclub.com/nlnapi/reset-password.php';

            console.log('📤 Sending forgot password request to:', url);
            console.log('📤 With email:', email);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    email: email,
                    password: tempPassword
                })
            });

            const text = await response.text();
            console.log('📥 Raw response:', text);

            let data;
            let isSuccess = false;
            let message = '';

            try {
                data = JSON.parse(text);
                isSuccess = data.success;
                message = data.message || '';
                console.log('✅ Parsed JSON:', data);
            } catch (e) {
                console.log('⚠️ Not JSON, checking for HTML response');
                
                if (text.includes('Password updated') || text.includes('✅ Password updated')) {
                    isSuccess = true;
                    message = 'Password reset successfully!';
                } else if (text.includes('User not found')) {
                    isSuccess = false;
                    message = 'User not found with this email. Please check and try again.';
                } else if (text.includes('not found')) {
                    isSuccess = false;
                    message = 'Email not found in our system.';
                } else {
                    message = 'Server returned an unexpected response. Please try again.';
                    isSuccess = false;
                }
            }

            if (isSuccess) {
                const resetToken = data?.reset_token || btoa(email + ':' + Date.now());
                
                return {
                    success: true,
                    message: message || `✅ Password has been reset! Temporary password: ${tempPassword}`,
                    reset_token: resetToken,
                    temporary_password: data?.new_password || tempPassword,
                    email: email
                };
            } else {
                return {
                    success: false,
                    message: message || 'Failed to reset password. Please try again.'
                };
            }

        } catch (error) {
            console.error('❌ Forgot password error:', error);
            return {
                success: false,
                message: 'Network error: ' + error.message
            };
        }
    },

    // ========== FIXED: Reset Password with Token ==========
    resetPassword: async(token, newPassword) => {
        console.log('🔄 Resetting password with token...');

        try {
            let email = '';
            try {
                const decoded = atob(token);
                const parts = decoded.split(':');
                if (parts.length > 0) {
                    email = parts[0];
                    console.log('✅ Extracted email from token:', email);
                }
            } catch (e) {
                console.warn('⚠️ Could not decode token, trying alternative method');
                const user = authAPI.getCurrentUser();
                if (user && user.email) {
                    email = user.email;
                    console.log('✅ Got email from localStorage:', email);
                }
            }

            if (!email) {
                return {
                    success: false,
                    message: 'Invalid reset token. Please request a new password reset.'
                };
            }

            const url = 'https://nlngraclub.com/nlnapi/reset-password.php';

            console.log('📤 Sending reset request to:', url);
            console.log('📤 For email:', email);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    email: email,
                    password: newPassword
                })
            });

            const text = await response.text();
            console.log('📥 Reset response:', text);

            let data;
            let isSuccess = false;
            let message = '';

            try {
                data = JSON.parse(text);
                isSuccess = data.success;
                message = data.message || '';
                console.log('✅ Parsed JSON:', data);
            } catch (e) {
                console.log('⚠️ Not JSON, checking for success text');
                if (text.includes('Password updated') || text.includes('✅ Password updated')) {
                    isSuccess = true;
                    message = 'Password updated successfully!';
                } else if (text.includes('not found')) {
                    isSuccess = false;
                    message = 'User not found. Please request a new password reset.';
                } else {
                    message = 'Server returned an unexpected response';
                }
            }

            if (isSuccess) {
                return {
                    success: true,
                    message: '✅ Password reset successfully! You can now login with your new password.'
                };
            } else {
                return {
                    success: false,
                    message: message || 'Failed to reset password'
                };
            }

        } catch (error) {
            console.error('❌ Reset password error:', error);
            return {
                success: false,
                message: 'Network error: ' + error.message
            };
        }
    },

    logout: async() => {
        console.log('🚪 Logging out...');

        localStorage.removeItem('nlngra_token');
        localStorage.removeItem('nlngra_jwt');
        localStorage.removeItem('nlngra_session');
        delete axios.defaults.headers.common['Authorization'];

        return { success: true, message: 'Logged out successfully' };
    },

    isLoggedIn: () => {
        const token = localStorage.getItem('nlngra_token');
        const user = localStorage.getItem('nlngra_user');
        const isLoggedIn = !!(token && user);
        console.log('🔍 Login status:', isLoggedIn ? 'Logged in' : 'Not logged in');
        return isLoggedIn;
    },

    getCurrentUser: () => {
        const userStr = localStorage.getItem('nlngra_user');
        const user = userStr ? JSON.parse(userStr) : null;
        console.log('👤 Current user:', user);
        return user;
    },

    getToken: () => {
        return localStorage.getItem('nlngra_token');
    }
};  // <-- THIS IS THE CLOSING BRACE FOR authAPI - MAKE SURE IT'S HERE

// ========== MEMBERS API ==========
export const memberAPI = {
    getById: async(id) => {
        console.log(`👤 Getting member ${id}...`);

        try {
            const currentUser = authAPI.getCurrentUser();
            if (currentUser && currentUser.id == id) {
                return {
                    success: true,
                    data: currentUser,
                    message: 'Member loaded successfully'
                };
            }

            return {
                success: false,
                message: 'Member endpoint not available in current API',
                data: null
            };
        } catch (error) {
            console.error('Get member error:', error);
            return {
                success: false,
                message: 'Failed to get member'
            };
        }
    },

    getAll: async() => {
        console.log('👥 Getting all members...');

        try {
            return {
                success: false,
                message: 'Members endpoint not implemented in API. Add to PHP first.',
                data: []
            };
        } catch (error) {
            console.error('Get all members error:', error);
            return {
                success: false,
                message: 'Failed to get members',
                data: []
            };
        }
    },

    update: async(id, memberData) => {
        return await authAPI.updateProfile(memberData);
    },

    delete: async(id) => {
        console.log(`🗑️ Deleting member ${id}...`);

        return {
            success: false,
            message: 'Delete member endpoint not implemented in PHP API'
        };
    },

    getFamilyMembers: async(primaryMemberId) => {
        return await familyAPI.getFamilyMembers();
    },

    removeFamilyMember: async(connectionId) => {
        return await familyAPI.removeFamilyMember(connectionId);
    }
};

// ========== FAMILY MEMBERS API ==========
export const familyAPI = {
    getFamilyMembers: async() => {
        console.log('👨‍👩‍👧‍👦 Getting family members...');

        try {
            const url = getFetchUrl('family/list');
            const token = localStorage.getItem('nlngra_jwt');

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                }
            });

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    dependents: data.data?.dependents || [],
                    count: data.data?.count || 0,
                    message: data.message || 'Family members loaded successfully'
                };
            } else {
                return {
                    success: false,
                    message: data.message || 'Failed to get family members',
                    dependents: []
                };
            }
        } catch (error) {
            console.error('Get family members error:', error);
            return {
                success: false,
                message: 'Failed to get family members',
                dependents: []
            };
        }
    },

    addFamilyMember: async(familyData) => {
        console.log('➕ Adding family member...', familyData);

        try {
            const url = getFetchUrl('family/add');
            const token = localStorage.getItem('nlngra_jwt');

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify(familyData)
            });

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    message: data.message || 'Family member added successfully',
                    dependent: data.data?.dependent
                };
            } else {
                return {
                    success: false,
                    message: data.message || 'Failed to add family member'
                };
            }
        } catch (error) {
            console.error('Add family member error:', error);
            return {
                success: false,
                message: 'Failed to add family member'
            };
        }
    },

    updateFamilyMember: async(dependentData) => {
        console.log('✏️ Updating family member...', dependentData);

        try {
            const url = getFetchUrl('family/update');
            const token = localStorage.getItem('nlngra_jwt');

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify(dependentData)
            });

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    message: data.message || 'Family member updated successfully'
                };
            } else {
                return {
                    success: false,
                    message: data.message || 'Failed to update family member'
                };
            }
        } catch (error) {
            console.error('Update family member error:', error);
            return {
                success: false,
                message: 'Failed to update family member'
            };
        }
    },

    removeFamilyMember: async(dependentId) => {
        console.log(`➖ Removing family member ${dependentId}...`);

        try {
            const url = getFetchUrl('family/remove');
            const token = localStorage.getItem('nlngra_jwt');

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify({ dependent_id: dependentId })
            });

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    message: data.message || 'Family member removed successfully'
                };
            } else {
                return {
                    success: false,
                    message: data.message || 'Failed to remove family member'
                };
            }
        } catch (error) {
            console.error('Remove family member error:', error);
            return {
                success: false,
                message: 'Failed to remove family member'
            };
        }
    }
};

// ========== EVENTS API ==========
export const eventAPI = {
    getAll: async(params = {}) => {
        console.log('📅 Fetching events...', params);

        try {
            let url = getFetchUrl('events');
            if (params.upcoming !== undefined) {
                url += `&upcoming=${params.upcoming}`;
            }
            if (params.limit) {
                url += `&limit=${params.limit}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    events: data.events || [],
                    count: data.count || 0,
                    message: 'Events loaded successfully'
                };
            } else {
                return {
                    success: false,
                    message: data.error || 'Failed to load events',
                    events: []
                };
            }
        } catch (error) {
            console.error('Get events error:', error);
            return {
                success: false,
                message: 'Failed to load events',
                events: []
            };
        }
    },

    getUpcoming: async(limit = 10) => {
        return eventAPI.getAll({ upcoming: true, limit: limit });
    },

    getById: async(id) => {
        try {
            const allEvents = await eventAPI.getAll();

            if (!allEvents.success) {
                throw new Error(allEvents.message || 'Failed to fetch events');
            }

            const event = allEvents.events.find(e => e.id === parseInt(id));

            if (event) {
                return {
                    success: true,
                    data: event,
                    source: 'filtered'
                };
            } else {
                throw new Error(`Event with ID ${id} not found`);
            }
        } catch (error) {
            console.error('Error fetching event:', error);
            throw error;
        }
    },

    registerForEvent: async(eventId, guestsCount = 0, specialRequirements = '') => {
        console.log(`📝 Registering for event ${eventId}...`);

        try {
            const url = getFetchUrl('events/register');
            const token = localStorage.getItem('nlngra_jwt');

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify({
                    event_id: eventId,
                    guests_count: guestsCount,
                    special_requirements: specialRequirements
                })
            });

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    message: data.message || 'Successfully registered for event'
                };
            } else {
                return {
                    success: false,
                    message: data.error || 'Failed to register for event'
                };
            }
        } catch (error) {
            console.error('Event registration error:', error);
            return {
                success: false,
                message: 'Failed to register for event'
            };
        }
    },

    getMemberEvents: async() => {
        try {
            const dashboard = await authAPI.getDashboard();
            if (dashboard.success) {
                return {
                    success: true,
                    events: dashboard.dashboard?.upcoming_events || [],
                    message: 'Member events loaded'
                };
            } else {
                throw new Error('Failed to load dashboard');
            }
        } catch (error) {
            console.error('Get member events error:', error);
            return {
                success: false,
                events: [],
                message: 'Failed to get member events'
            };
        }
    }
};

// ========== ACTIVITIES API ==========
export const activityAPI = {
    getMemberActivities: async(limit = 20) => {
        console.log(`📝 Getting member activities...`);

        try {
            const url = getFetchUrl(`activities&limit=${limit}`);
            const token = localStorage.getItem('nlngra_jwt');

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                }
            });

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    activities: data.activities || [],
                    count: data.count || 0,
                    message: 'Activities loaded successfully'
                };
            } else {
                return {
                    success: false,
                    message: data.error || 'Failed to load activities',
                    activities: []
                };
            }
        } catch (error) {
            console.error('Get activities error:', error);
            return {
                success: false,
                message: 'Failed to load activities',
                activities: []
            };
        }
    }
};

// ========== STATISTICS API ==========
export const statsAPI = {
    get: async() => {
        console.log('📊 Fetching statistics...');

        try {
            const url = getFetchUrl('stats');

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    stats: data.stats,
                    message: 'Statistics loaded successfully'
                };
            } else {
                return {
                    success: false,
                    message: data.error || 'Failed to load statistics',
                    stats: null
                };
            }
        } catch (error) {
            console.error('Get statistics error:', error);
            return {
                success: false,
                message: 'Failed to load statistics',
                stats: null
            };
        }
    }
};

// ========== ANNOUNCEMENTS API ==========
export const announcementAPI = {
    getAll: async(limit = 10) => {
        console.log('📢 Fetching announcements...');

        try {
            const url = getFetchUrl(`announcements&limit=${limit}`);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    announcements: data.announcements || [],
                    count: data.count || 0,
                    message: 'Announcements loaded successfully'
                };
            } else {
                return {
                    success: false,
                    message: data.error || 'Failed to load announcements',
                    announcements: []
                };
            }
        } catch (error) {
            console.error('Get announcements error:', error);
            return {
                success: false,
                message: 'Failed to load announcements',
                announcements: []
            };
        }
    }
};

// ========== SYSTEM API ==========
export const systemAPI = {
    health: async() => {
        console.log('🏥 Checking API health...');

        try {
            const url = getFetchUrl('health');

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            const data = await response.json();

            return {
                success: true,
                data: data,
                message: 'API is healthy',
                timestamp: new Date().toISOString()
            };
        } catch (error) {
            console.error('Health check error:', error);
            return {
                success: false,
                message: 'Health check failed',
                error: error.message
            };
        }
    },

    dbCheck: async() => {
        console.log('🗄️ Checking database...');

        try {
            const url = getFetchUrl('db');

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    data: data,
                    message: 'Database connected successfully'
                };
            } else {
                return {
                    success: false,
                    error: data.error,
                    message: 'Database check failed'
                };
            }
        } catch (error) {
            console.error('Database check error:', error);
            return {
                success: false,
                message: 'Database check failed',
                error: error.message
            };
        }
    }
};

// ========== INITIALIZATION ==========
export const initializeAuth = () => {
    const token = localStorage.getItem('nlngra_jwt');
    if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        console.log('🔑 Auth token initialized');
    } else {
        console.log('🔒 No auth token found');
    }
};

// ========== TEST FUNCTIONS ==========
export const testConnection = async() => {
    return await systemAPI.health();
};

export const testAllAPIs = async() => {
    console.log('🚀 Starting API tests...');

    const results = {
        timestamp: new Date().toISOString(),
        tests: {},
        summary: ''
    };

    try {
        console.log('1. Testing API Health...');
        results.tests.health = await systemAPI.health();

        console.log('2. Testing Database...');
        results.tests.database = await systemAPI.dbCheck();

        const allTests = Object.values(results.tests);
        const successfulTests = allTests.filter(test => test.success);
        const failedTests = allTests.filter(test => !test.success);

        results.success = failedTests.length === 0;
        results.passed = successfulTests.length;
        results.total = allTests.length;
        results.failed = failedTests.length;

        if (results.success) {
            results.summary = `✅ ALL TESTS PASSED! ${results.passed}/${results.total} tests successful.`;
        } else {
            results.summary = `⚠️ ${results.failed} TEST(S) FAILED! ${results.passed}/${results.total} passed.`;
        }

        console.log(results.summary);

        return results;

    } catch (error) {
        console.error('❌ Test failed:', error);

        results.success = false;
        results.error = error.message;
        results.summary = `❌ TEST FAILED: ${error.message}`;

        return results;
    }
};

export const quickTest = async() => {
    return await systemAPI.health();
};

export const checkApiStatus = async() => {
    const result = await systemAPI.health();
    return {
        online: result.success,
        data: result.data,
        timestamp: new Date().toISOString(),
        message: result.message
    };
};

// ========== HELPER FUNCTIONS ==========
export const getDashboardData = async() => {
    return await authAPI.getDashboard();
};

export const getFamilyData = async() => {
    return await familyAPI.getFamilyMembers();
};

export const getUpcomingEvents = async(limit = 5) => {
    return await eventAPI.getUpcoming(limit);
};

export const getRecentAnnouncements = async(limit = 5) => {
    return await announcementAPI.getAll(limit);
};

// ========== INITIALIZE ==========
initializeAuth();

// ========== EXPORT EVERYTHING ==========
export default {
    testAllAPIs,
    quickTest,
    checkApiStatus,
    initializeAuth,
    testConnection,

    authAPI,
    memberAPI,
    familyAPI,
    eventAPI,
    activityAPI,
    statsAPI,
    announcementAPI,
    systemAPI,

    getDashboardData,
    getFamilyData,
    getUpcomingEvents,
    getRecentAnnouncements
};